import { z } from 'zod';

/**
 * Zod schemas for TurnOutcome. Used at the socket boundary to validate
 * that sandbox → web messages conform to the discriminated-union shape.
 *
 * Kept in sync with `packages/shared/src/types/turn-outcome.ts`.
 */

const RateLimitInfoSummarySchema = z.object({
  status: z.enum(['allowed', 'allowed_warning', 'rejected']),
  resetsAt: z.number().optional(),
  rateLimitType: z
    .enum([
      'five_hour',
      'seven_day',
      'seven_day_opus',
      'seven_day_sonnet',
      'overage',
      // Added by @anthropic-ai/claude-agent-sdk 0.3.198 (SDKRateLimitInfo).
      'seven_day_overage_included',
    ])
    .optional(),
  utilization: z.number().optional(),
});

const DeferredToolUseSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  input: z.record(z.string(), z.unknown()),
});

const ModelUsageEntrySchema = z.object({
  inputTokens: z.number(),
  outputTokens: z.number(),
  cacheReadInputTokens: z.number(),
  cacheCreationInputTokens: z.number(),
  webSearchRequests: z.number(),
  costUSD: z.number(),
  contextWindow: z.number().optional(),
  maxOutputTokens: z.number().optional(),
});

const TerminationRawSchema = z.object({
  subtype: z.string(),
  terminal_reason: z.string().optional(),
  stop_reason: z.string().nullable().optional(),
  api_error_status: z.number().nullable().optional(),
  errors: z.array(z.string()),
  last_per_turn_error: z
    .enum([
      'authentication_failed',
      'billing_error',
      'rate_limit',
      'invalid_request',
      'server_error',
      'unknown',
      'max_output_tokens',
      // Added in SDK 0.3.144 — classification is a follow-up (P1.5-7).
      'oauth_org_not_allowed',
      'model_not_found',
      // Added in SDK 0.3.177 — Anthropic 529 transient overload (grouped with server_error).
      'overloaded',
      // Claude Code 2.1.284 — account states the person fixes on their own account.
      'account_on_hold',
      'verification_required',
      'cloud_credential_error',
    ])
    .optional(),
  last_rate_limit_info: RateLimitInfoSummarySchema.optional(),
});

export const TurnOutcomeSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('completed'),
    summary: z.string(),
    /** The final reply alone, with no earlier round's narration; null when the turn ended with no final text. */
    answer: z.string().nullable().optional(),
    costUsd: z.number(),
    numTurns: z.number(),
    durationMs: z.number(),
    modelUsage: z.record(z.string(), ModelUsageEntrySchema),
    structuredOutput: z.unknown().optional(),
    raw: TerminationRawSchema,
  }),
  z.object({
    kind: z.literal('refused'),
    text: z.string(),
    raw: TerminationRawSchema,
  }),
  z.object({
    kind: z.literal('deferred'),
    toolUse: DeferredToolUseSummarySchema,
    raw: TerminationRawSchema,
  }),
  z.object({
    kind: z.literal('max_turns'),
    numTurns: z.number(),
    raw: TerminationRawSchema,
  }),
  z.object({
    kind: z.literal('max_budget'),
    costUsd: z.number(),
    triggeredBy: z.enum(['credits', 'task_limit', 'turn_ceiling', 'daily_ceiling', 'agent_month']).optional(),
    raw: TerminationRawSchema,
  }),
  z.object({
    kind: z.literal('max_structured_output_retries'),
    raw: TerminationRawSchema,
  }),
  z.object({
    kind: z.literal('user_aborted'),
    abortPhase: z.enum(['streaming', 'tools']),
    raw: TerminationRawSchema,
  }),
  z.object({
    kind: z.literal('hook_blocked'),
    hookKind: z.enum(['stop', 'other']),
    stopReason: z.string().optional(),
    raw: TerminationRawSchema,
  }),
  z.object({
    kind: z.literal('task_cap'),
    message: z.string(),
    raw: TerminationRawSchema,
  }),
  z.object({
    kind: z.literal('detached'),
    raw: TerminationRawSchema,
  }),
  z.object({
    kind: z.literal('context_exceeded'),
    raw: TerminationRawSchema,
  }),
  z.object({
    kind: z.literal('rate_limited'),
    rateLimitInfo: RateLimitInfoSummarySchema.optional(),
    raw: TerminationRawSchema,
  }),
  z.object({
    kind: z.literal('image_error'),
    message: z.string(),
    raw: TerminationRawSchema,
  }),
  z.object({
    kind: z.literal('request_invalid'),
    message: z.string(),
    raw: TerminationRawSchema,
  }),
  z.object({
    kind: z.literal('model_error'),
    message: z.string(),
    raw: TerminationRawSchema,
  }),
  z.object({
    kind: z.literal('model_unavailable'),
    message: z.string(),
    raw: TerminationRawSchema,
  }),
  z.object({
    kind: z.literal('auth_error'),
    // Optional engine-supplied copy (Claude Code engine). Absent on every other arm.
    message: z.string().optional(),
    raw: TerminationRawSchema,
  }),
  z.object({
    kind: z.literal('billing_error'),
    // Whose account ran dry: absent = ours (a native lane's key), 'person' = their own plan.
    owner: z.literal('person').optional(),
    // Engine-supplied copy for an owner:'person' failure. Absent on the native lanes.
    message: z.string().optional(),
    raw: TerminationRawSchema,
  }),
  z.object({
    kind: z.literal('network_error'),
    message: z.string(),
    raw: TerminationRawSchema,
  }),
  z.object({
    kind: z.literal('unknown'),
    subtype: z.string(),
    terminalReason: z.string().optional(),
    errors: z.array(z.string()),
    raw: TerminationRawSchema,
  }),
]);
